import { IPreviewImage } from "@evergis/ui";
import { useCallback, useMemo, useState } from "react";
import { api } from "../../../../../../evergis/api";
import { JSONAttributeValue } from "../../types";
import { getFileType } from "./getFileType";
import { getFileTypeIcon } from "./getFileTypeIcon";
import { IMAGE_FILE_TYPES } from "./types";

// eslint-disable-next-line max-lines-per-function
export const useAttachmentItems = (items: JSONAttributeValue[], onChange) => {
  const [page, setPage] = useState(1);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewItems, setPreviewItems] = useState<IPreviewImage[]>([]);

  const allPreviewItems = useMemo(
    () =>
      items?.map(({ link, isExternal, mimeType, name }) => {
        const fileType = getFileType(mimeType, name);
        const isImg = IMAGE_FILE_TYPES.includes(fileType);

        return {
          src: isExternal && isImg ? link : isImg ? `${api.url}/resources/${link}/download` : getFileTypeIcon(fileType),
          fileName: name,
          downloadLink: isExternal ? link : `${api.url}/resources/${link}/download`,
          slideHeight: isImg ? "4.5rem" : "3rem",
          slideWidth: isImg ? "4.5rem" : "3rem",
        };
      }),
    [items],
  );

  const onPreview = useCallback(
    async (name: string) => {
      const item = allPreviewItems?.find(({ fileName }) => fileName === name);
      const newItems = [item, ...allPreviewItems?.filter(({ fileName }) => fileName !== name)];

      setPreviewItems(newItems);
      setPreviewOpen(true);
    },
    [allPreviewItems],
  );

  const onAdd = useCallback(
    (newItems: JSONAttributeValue[]) => {
      onChange([...(items || []), ...newItems]);
    },
    [onChange, items],
  );

  const onDelete = useCallback(
    (link: string) => {
      onChange(items?.filter(({ link: itemLink }) => itemLink !== link));
    },
    [onChange, items],
  );

  const onShowMore = useCallback(() => {
    setPage(currentPage => currentPage + 1);
  }, []);

  return {
    onAdd,
    onDelete,
    onShowMore,
    page,
    onPreview,
    previewOpen,
    setPreviewOpen,
    previewItems,
  };
};
